import lodash from 'lodash';
import markdownIt from 'markdown-it';
import {toJS} from 'mobx';

import {updateEntity} from '../../ecs/entityHelper';
import {fontStylesPlugin} from '../../../utils/markdown/markdownStylesPlugin';
import {underlinePlugin} from '../../../utils/markdown/markdownUnderlinePlugin';
import {parseMarkdownToWords} from '../../../utils/markdown/markdownWordParser';

/**
 * The default blank drop shadow object.
 * @type {{blur: number, color: string, x: number, y: number}}
 */
export const DEFAULT_DROP_SHADOW = {
  blur: 0,
  color: '#000',
  x: 0,
  y: 0,
};

/**
 * The default blank stroke.
 * @type {{width: number, color: string}}
 */
export const DEFAULT_STROKE = {
  width: 0,
  color: '#000',
};

/**
 * The conversion factor for going from milliseconds to seconds.
 *
 * @const {number}
 */
const MS_TO_SECONDS = 1000;

const markdownParser = markdownIt({
  html: false,
  xhtmlOut: true,
  breaks: true,
});

// Add the markdown-it plugins.
markdownParser.use(fontStylesPlugin);
markdownParser.use(underlinePlugin);

/**
 * The text component.
 *
 * @param {Array.<Array.<{}>>} lines
 * @param {{}} textData
 * @param {string} textData.markdown
 * @param {string=} textData.variableName
 * @param {number=} textData.dropShadowBlur
 * @param {string=} textData.dropShadowColor
 * @param {number=} textData.dropShadowX
 * @param {number=} textData.dropShadowY
 * @param {string=} textData.strokeColor
 * @param {number=} textData.strokeWidth
 * @param {number=} textData.opacity
 * @returns {{text: {lines: Array.<Array.<{}>>, markdown: string, variableName: string}}}
 */
export function textComponent(lines, textData) {
  const {markdown, variableName} = textData;

  const opacity = (textData.opacity === undefined) ? 1 : textData.opacity;
  const stroke = lodash.defaults(
    lodash.pick(textData.stroke, lodash.keys(DEFAULT_STROKE)),
    DEFAULT_STROKE
  );
  const dropShadow = lodash.defaults(
    lodash.pick(textData.dropShadow, lodash.keys(DEFAULT_DROP_SHADOW)),
    DEFAULT_DROP_SHADOW
  );

  return {
    text: {
      lines,
      markdown,
      dropShadow,
      opacity,
      stroke,
      variableName: variableName || null,
      default: {
        lines: lodash.cloneDeep(lines),
        markdown,
        dropShadow,
        opacity,
        stroke,
      },
      transition: null,
    },
  };
}

/**
 * Gets the text component from the source item.
 *
 * @param {{text: {plaintext: ?string, markdown: ?string}}} item
 * @param {{}=} variables
 * @returns {{text: {isHtml: boolean, value: string}}}
 */
export function getTextFromSource(item, variables) {
  if (!item.text) {
    return {};
  }

  const text = item.text;

  let markdown = text.markdown || text.plaintext || '';

  const canEdit = lodash.get(item, 'compose.canEdit');
  const variableName = lodash.get(item, 'compose.variableName');
  if (canEdit && variableName) {
    const variableValue = lodash.get(variables, `text.${variableName}`, '');
    if (variableValue && lodash.isString(variableValue)) {
      // The old deprecated variable style is a string that is just the markdown.
      markdown = variableValue;
    } else if (variableValue) {
      // The ()s fix an eslint parsing error.
      ({markdown} = variableValue);
    }
  }

  const lines = parseMarkdownToWords(markdown, null, text);

  const textData = {
    markdown,
    variableName,
    dropShadow: text.dropShadow || undefined,
    opacity: text.opacity,
    stroke: text.stroke || undefined,
  };

  return textComponent(lines, textData);
}

/**
 * Parses an entity back into source JSON.
 *
 * @param {{}} entity
 * @returns {{}}
 */
export function getTextForSource(entity) {
  if (!entity.has('text')) {
    return {};
  }

  const compose = entity.get('compose');
  const text = entity.get('text');

  const output = {};

  const canEdit = lodash.get(compose, 'canEdit', false);
  const variableName = lodash.get(compose, 'variableName');
  if (!canEdit || !variableName) {
    output.markdown = text.markdown || '';
  }

  if (text.stroke) {
    output.stroke = toJS(text.stroke);
  }
  if (text.dropShadow) {
    output.dropShadow = toJS(text.dropShadow);
  }
  if (text.opacity !== undefined) {
    output.opacity = text.opacity;
  }

  return {
    text: lodash.cloneDeep(output),
  };
}

/**
 * Parses the given text from markdown to HTML.
 *
 * @param {string} markdownText
 * @returns {string}
 */
export function parseMarkdownToHtml(markdownText) {
  const html = markdownParser.render(markdownText);
  return lodash.trim(html).replace(/<p/g, '<div').replace(/<\/p/g, '</div');
}

/**
 * Updates variables in this component.
 *
 * @param {{}} text
 * @param {{}} variables
 * @returns {{}}
 */
export function updateTextVariables(text, variables) {
  const newText = lodash.cloneDeep(toJS(text));

  const markdown = text.default.markdown || '';

  let safeVariables = variables;
  if (text.variableName && !safeVariables[text.variableName]) {
    safeVariables = {...variables, [text.variableName]: ''};
  }

  const compiledValue = String(markdown).replace(/\|\|=([^|]+?)\|\|/g, (matched, variableName) => {
    if (safeVariables[variableName] === undefined) {
      return '';
    }
    return safeVariables[variableName];
  });

  newText.markdown = compiledValue;
  newText.lines = parseMarkdownToWords(compiledValue, null, newText);

  return newText;
}

/**
 * Creates the text transition object for the entity.
 *
 * @param {{}} entity
 * @param {string} splitOn
 * @returns {Array.<{}>}
 */
export function setupTransition(entity, splitOn) {
  const currentText = entity.get('text');
  if (currentText.transition && currentText.transition.splitOn === splitOn) {
    return currentText.transition.default;
  }

  // Initialize the text words into letters, words, or word blocks for the transition.
  const transitionLines = parseMarkdownToWords(currentText.markdown, splitOn, toJS(currentText));
  const textTransitionData = {
    lines: transitionLines,
    default: lodash.clone(transitionLines),
    splitOn,
  };

  updateEntity(entity, 'text', {transition: textTransitionData}, 'textComponentSetupTransition');

  return textTransitionData.default;
}

/**
 * Removes the transition object from the entity.
 * This should happen after the transitions for the entity are finished.
 *
 * @param {{}} entity
 */
export function removeTransition(entity) {
  const currentText = entity.get('text');
  if (!currentText.transition) {
    return;
  }

  updateEntity(entity, 'text', {transition: null}, 'textComponentRemoveTransition');
}

/**
 * Parse the transitionDetails into individual transitions for each text word.
 *
 * @param {{}} transitionDetails
 * @param {Array.<{}>} textLines
 * @param {string} propertyPrefix Only parse properties with this prefix.
 * @returns {{}}
 */
export function parseLinesIntoWordTransitions(transitionDetails, textLines, propertyPrefix) {
  const wordPaths = [];
  lodash.forEach(textLines, (lineDetails, lineIndex) => {
    lodash.forEach(lineDetails.words, (wordDetails, wordIndex) => {
      wordPaths.push({
        path: `[${lineIndex}].words[${wordIndex}]`,
        text: wordDetails.text,
      });
    });
  });

  const wordCount = wordPaths.length;

  const wordDetails = [];
  lodash.forEach(transitionDetails, (tweenDetails) => {
    const {propertyName} = tweenDetails;

    if (!lodash.startsWith(propertyName, propertyPrefix)) {
      // Only do word parsing for word (letter, word, line) transitions.
      wordDetails.push(tweenDetails);
      return;
    }

    const isStaggered = (tweenDetails.stagger !== false);

    const tweenDuration = tweenDetails.duration;

    const smallestDuration = 0.05;

    const isStaggerDefined = !isNaN(parseInt(tweenDetails.stagger, 10));

    let staggerDelay = 0;
    if (isStaggered) {
      if (!isStaggerDefined) {
        staggerDelay = (tweenDuration / wordCount);
      } else {
        staggerDelay = Number(tweenDetails.stagger) / MS_TO_SECONDS;

        if (Math.abs(staggerDelay * wordCount) > tweenDuration - smallestDuration) {
          // Make sure the stagger delay doesn't reduce this items duration below the smallest duration.
          staggerDelay = staggerDelay < 0
            ? -1 * (tweenDuration - smallestDuration) / wordCount
            : (tweenDuration - smallestDuration) / wordCount;
        }
      }
    }

    let duration = tweenDuration;
    if (isStaggered) {
      const staggerDuration = parseInt(tweenDetails.staggerDuration, 10);
      const isStaggerDurationDefined = !isNaN(staggerDuration);

      if (isStaggerDurationDefined) {
        duration = staggerDuration / MS_TO_SECONDS;
      } else if (!isStaggerDefined) {
        duration = staggerDelay;
      } else {
        duration = tweenDuration - (Math.abs(staggerDelay) * (wordCount - 1));
      }
    }

    /**
     * Notes: Move this to documentation somewhere!
     * Stagger
     * If === false, there will be no staggering.
     * If not a number or === true, it will stagger evenly for each item
     *   (i.e. 1 second - 2 items [0, .5], 3 items [0, .33, .66], etc).
     * If a number, it will delay each number by that number of milliseconds
     *   (i.e. 500 ms - 2 items [0, .5], 3 items [0, .5, 1], etc).
     *
     * Stagger Duration
     * If stagger === false, duration = the whole tween duration
     * if a number, it will run for that number of milliseconds
     * if not a number and stagger is not a number, duration = stagger, it will run until the next item starts
     *   (items will animate one after the other).
     * if not a number and stagger is a number, after it starts, it will run until the end of the tween duration
     */

    lodash.forEach(wordPaths, (pathData, wordIndex) => {
      if (shouldFilterOutTween(tweenDetails, pathData, wordIndex)) {
        return;
      }

      wordDetails.push({
        ...tweenDetails,
        path: pathData.path,
        delay: staggerDelay < 0
          ? (tweenDetails.delay || 0) + (Math.abs(staggerDelay) * (wordCount - 1 - wordIndex))
          : (tweenDetails.delay || 0) + (staggerDelay * wordIndex),
        duration,
      });
    });
  });

  return wordDetails;
}

/**
 * Determines whether or not the tween should be filtered out.
 *
 * @param {{}} tweenDetails
 * @param {{path: string, text: string}} pathData
 * @param {number} wordIndex
 * @returns {boolean} True to filter out, false to leave in.
 */
function shouldFilterOutTween(tweenDetails, pathData, wordIndex) {
  if (!tweenDetails.filters || !Array.isArray(tweenDetails.filters)) {
    return false;
  }

  return lodash.reduce(tweenDetails.filters, (shouldFilter, filterName) => {
    if (filterName === 'odd') {
      if (!wordIndex || wordIndex % 2 === 0) {
        return true;
      }
    } else if (filterName === 'even') {
      if (wordIndex % 2 === 1) {
        return true;
      }
    }

    const numberMatch = filterName.match(/^number\(([0-9,]+)\)$/);
    if (numberMatch) {
      const allowedNumbers = numberMatch[1].split(',').map((item) => Number(item));
      if (!lodash.includes(allowedNumbers, wordIndex + 1)) {
        return true;
      }
    }

    return shouldFilter;
  }, false);
}
